import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, MessageSquare, Power, QrCode, RotateCcw, LogOut, CheckCircle2, Send } from "lucide-react";
import { whatsappService } from "@/services/whatsapp";
import { WhatsAppQRModal } from "@/components/whatsapp/WhatsAppQRModal";
import { toast } from "sonner";

const WA_STATUS_MAP: Record<string, { label: string; className: string; description: string }> = {
  WORKING: {
    label: "Conectado",
    className: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
    description: "Tu WhatsApp está conectado y enviando mensajes.",
  },
  SCAN_QR_CODE: {
    label: "Esperando escaneo",
    className: "bg-amber-500/15 text-amber-500 border-amber-500/30",
    description: "Escanea el código QR con tu WhatsApp para completar la conexión.",
  },
  STARTING: {
    label: "Iniciando",
    className: "bg-amber-500/15 text-amber-500 border-amber-500/30",
    description: "La sesión se está iniciando, esto puede tardar unos segundos.",
  },
  STOPPED: {
    label: "Detenido",
    className: "bg-muted text-muted-foreground border-border",
    description: "La sesión está detenida. Inicia sesión para generar el código QR.",
  },
  FAILED: {
    label: "Error",
    className: "bg-destructive/15 text-destructive border-destructive/30",
    description: "La sesión falló. Reinicia la sesión para generar un nuevo código QR.",
  },
};

export function WhatsAppConnectionTab() {
  const queryClient = useQueryClient();
  const [qrOpen, setQrOpen] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("");

  const { data: session, isLoading, isError } = useQuery({
    queryKey: ["whatsapp-session"],
    queryFn: async () => {
      try {
        return await whatsappService.getSessionStatus();
      } catch {
        return null;
      }
    },
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "STARTING" || status === "SCAN_QR_CODE" ? 3000 : false;
    },
  });

  const invalidateSession = () => queryClient.invalidateQueries({ queryKey: ["whatsapp-session"] });

  const startMutation = useMutation({
    mutationFn: whatsappService.startSession,
    onSuccess: () => {
      toast.success("Sesión de WhatsApp iniciada.");
      invalidateSession();
    },
    onError: () => toast.error("No se pudo iniciar la sesión de WhatsApp."),
  });

  const restartMutation = useMutation({
    mutationFn: whatsappService.restartSession,
    onSuccess: () => {
      toast.success("Sesión de WhatsApp reiniciada.");
      invalidateSession();
    },
    onError: () => toast.error("No se pudo reiniciar la sesión de WhatsApp."),
  });

  const logoutMutation = useMutation({
    mutationFn: whatsappService.logoutSession,
    onSuccess: () => {
      toast.success("Sesión de WhatsApp cerrada.");
      invalidateSession();
    },
    onError: () => toast.error("No se pudo cerrar la sesión de WhatsApp."),
  });

  const sendTestMutation = useMutation({
    mutationFn: () => whatsappService.sendMessage({ chatId: `${testPhone.trim()}@c.us`, text: testMessage.trim() }),
    onSuccess: () => {
      toast.success("Mensaje de prueba enviado.");
      setTestMessage("");
    },
    onError: () => toast.error("No se pudo enviar el mensaje de prueba."),
  });

  const anyMutationPending =
    startMutation.isPending || restartMutation.isPending || logoutMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Cargando estado de la sesión...
      </div>
    );
  }

  if (isError || !session) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
        <MessageSquare className="w-8 h-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">
          No hay configuración de WhatsApp activa. Por favor, comuníquese con su proveedor.
        </p>
      </div>
    );
  }

  const statusInfo = WA_STATUS_MAP[session.status] ?? {
    label: session.status,
    className: "bg-muted text-muted-foreground border-border",
    description: "",
  };

  return (
    <div className="space-y-4 py-2">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/20 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
            <MessageSquare className="w-4 h-4 text-emerald-500" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${statusInfo.className}`}>
                {statusInfo.label}
              </span>
            </div>
            {session.status === "WORKING" && session.me?.pushName ? (
              <p className="text-xs text-muted-foreground mt-1">
                Conectado como <span className="font-medium text-foreground">{session.me.pushName}</span>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">{statusInfo.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {session.status === "STOPPED" && (
            <Button
              size="sm"
              onClick={() => startMutation.mutate()}
              disabled={anyMutationPending}
            >
              {startMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Power className="w-3.5 h-3.5 mr-1.5" />
              )}
              Iniciar sesión
            </Button>
          )}

          {session.status === "FAILED" && (
            <Button
              size="sm"
              onClick={() => restartMutation.mutate()}
              disabled={anyMutationPending}
            >
              {restartMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              )}
              Reiniciar sesión
            </Button>
          )}

          {session.status === "SCAN_QR_CODE" && (
            <Button
              variant="outline"
              size="sm"
              className="border-green-500/40 text-green-500 hover:bg-green-500/10 hover:border-green-500/60 hover:text-green-500"
              onClick={() => setQrOpen(true)}
              disabled={anyMutationPending}
            >
              <QrCode className="w-3.5 h-3.5 mr-1.5" />
              Ver código QR
            </Button>
          )}

          {(session.status === "WORKING" || session.status === "SCAN_QR_CODE") && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => restartMutation.mutate()}
              disabled={anyMutationPending}
            >
              {restartMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              )}
              Reiniciar
            </Button>
          )}

          {session.status === "WORKING" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:border-destructive/60 hover:text-destructive"
                  disabled={anyMutationPending}
                >
                  <LogOut className="w-3.5 h-3.5 mr-1.5" />
                  Cerrar sesión
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Cerrar sesión de WhatsApp?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Se desconectará el número vinculado y dejarán de enviarse mensajes hasta que vuelvas a escanear el código QR.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => logoutMutation.mutate()}
                    disabled={logoutMutation.isPending}
                    className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                  >
                    {logoutMutation.isPending ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Cerrando...</>
                    ) : "Sí, cerrar sesión"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {session.status === "STARTING" && (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Iniciando...
            </span>
          )}

          {session.status === "WORKING" && (
            <span className="flex items-center gap-1 text-xs text-emerald-500 font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      </div>

      {session.status === "WORKING" && (
        <div className="rounded-lg border border-border bg-muted/10 px-4 py-3 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Enviar mensaje de prueba
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="test-phone">Número de teléfono</Label>
              <Input
                id="test-phone"
                type="tel"
                placeholder="Ej: 573001234567"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value.replace(/[^\d]/g, ""))}
              />
              <p className="text-xs text-muted-foreground">
                Incluye el código de país sin el símbolo +
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="test-message">Mensaje</Label>
              <Textarea
                id="test-message"
                placeholder="Escribe un mensaje de prueba..."
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => sendTestMutation.mutate()}
              disabled={!testPhone.trim() || !testMessage.trim() || sendTestMutation.isPending}
            >
              {sendTestMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5 mr-1.5" />
              )}
              Enviar prueba
            </Button>
          </div>
        </div>
      )}

      <WhatsAppQRModal open={qrOpen} onOpenChange={(open) => { setQrOpen(open); if (!open) invalidateSession(); }} />
    </div>
  );
}
